import { useState, useEffect, useRef } from "react";
import logo from "../../assets/images/ecom_tamil_logo.png";
import { 
  Mail, Lock, ArrowRight, Eye, EyeOff, User, 
  Shield, CheckCircle2, Phone, GraduationCap, 
  Users, TrendingUp, Sparkles, KeyRound, Loader2
} from "lucide-react";
import { useGoogleLogin } from '@react-oauth/google';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { postRequest } from "../../utils/networking";
import { apiUrls } from "../../utils/url";
import CommonToastContainer from "../../components/CommonToastContainer";
import websocketService from "../../utils/websocket";

export default function UserLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState("login"); // "login" | "signup"
  const [email, setEmail] = useState(() => location.state?.email || "");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [wsStatus, setWsStatus] = useState("disconnected");
  const passwordRef = useRef(null);

  useEffect(() => {
    if (location.state?.email) {
      setEmail(location.state.email);
      setStep("login");
      if (passwordRef.current) {
        passwordRef.current.focus();
      }
    }
  }, [location.state]);

  useEffect(() => {
    if (location.state?.message) {
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Check for pre-filled email from signup / session
  useEffect(() => {
    const prefilledEmail = sessionStorage.getItem('prefillEmail');
    if (prefilledEmail) {
      setEmail(prefilledEmail);
      setStep("login");
      sessionStorage.removeItem('prefillEmail');
      setTimeout(() => {
        showToast("Account created! Please login with your password.", "info");
      }, 100);
    }
  }, []);

  const showToast = (message, type = "success") => {
    if (type === "success") {
      toast.success(message);
    } else if (type === "error") {
      toast.error(message);
    } else if (type === "info") {
      toast.info(message);
    } else if (type === "warning") {
      toast.warning(message);
    }
  };

  useEffect(() => {
    const handleConnect = () => {
      console.log("WebSocket connected in login page");
      setWsStatus("connected");
    };

    const handleDisconnect = (data) => {
      console.log("WebSocket disconnected in login page:", data);
      setWsStatus("disconnected");
    };

    websocketService.on("connect", handleConnect);
    websocketService.on("disconnected", handleDisconnect);

    return () => {
      websocketService.off("connect", handleConnect);
      websocketService.off("disconnected", handleDisconnect);
    };
  }, []);

  const connectWebSocket = async (authToken, userId) => {
    try {
      setWsStatus("connecting");
      await websocketService.connect(authToken, userId);
      setWsStatus("connected");
      return true;
    } catch (err) {
      console.error("WebSocket connection failed:", err);
      setWsStatus("disconnected");
      return false;
    }
  };

  const completeLogin = async (loginData) => {
    const usertype = (loginData.userData.usertype !== undefined && loginData.userData.usertype !== null)
      ? loginData.userData.usertype.toString()
      : "2";

    const updatedUserData = {
      ...loginData.userData,
      usertype: usertype,
      role: usertype === "1" ? "Admin" : (usertype === "3" ? "Staff" : "Student")
    };

    localStorage.setItem("user", JSON.stringify(updatedUserData));
    localStorage.setItem("authToken", loginData.token);
    localStorage.setItem("usertype", usertype);

    if (rememberMe) {
      localStorage.setItem("rememberEmail", email);
    } else {
      localStorage.removeItem("rememberEmail");
    }

    showToast("Login successful! Redirecting...", "success");

    const redirectPath = localStorage.getItem('redirectAfterLogin');

    setTimeout(() => {
      if (redirectPath &&
        redirectPath !== '/login' &&
        redirectPath !== '/signin' &&
        redirectPath !== '/') {
        localStorage.removeItem('redirectAfterLogin');
        navigate(redirectPath);
      } else {
        if (usertype === "1") {
          navigate("/admin/courseslist");
        } else if (usertype === "3") {
          navigate("/solutions");
        } else {
          navigate("/courses");
        }
      }
    }, 1200);
  };

  const loginWithGoogle = useGoogleLogin({
    flow: 'implicit',
    onSuccess: async (tokenResponse) => {
      setIsLoading(true);
      const accessToken = tokenResponse.access_token;

      try {
        const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: { Authorization: `Bearer ${accessToken}` }
        });

        const userInfo = await userInfoResponse.json();
        const userName = userInfo.name;
        const userEmail = userInfo.email;

        if (userEmail) {
          const response = await postRequest(apiUrls.verifyuser, {
            email: userEmail,
            logintype: 2
          });

          if (response.status === "E" &&
            (response.message?.includes("Session Expired") ||
              response.message?.includes("logged in from another device"))) {
            showToast(response.message, "error");
            return;
          }

          if (response.status === "S" || response.status === "E") {
            const responseData = response.data?.[0] || {};
            const usertype = responseData.usertype || response.usertype || 2;
            
            if (usertype.toString() === "1") {
              showToast("Access denied for this user type", "error");
              return;
            }

            if (usertype.toString() !== "2" && usertype.toString() !== "3") {
              showToast("Email is invalid", "error");
              return;
            }

            const storedUsername = localStorage.getItem(`username_${userEmail}`) || responseData.username || userName;
            const userData = {
              id: responseData.userId || response.userId || userEmail,
              name: storedUsername,
              email: userEmail,
              logintype: 2,
              usertype: usertype
            };

            const authToken = response.token || accessToken;
            connectWebSocket(authToken, userData.id);
            await completeLogin({ userData, token: authToken });

          } else if (response.status === "N") {
            // New Google user - auto-register
            try {
              const registerResponse = await postRequest(apiUrls.registeruser, {
                email: userEmail,
                username: userName,
                logintype: 2,
                app: 2,
                usertype: 2
              });

              if (registerResponse.status === "S" || registerResponse.token) {
                const regData = registerResponse.data?.[0] || {};
                const regUsertype = regData.usertype || registerResponse.usertype || 2;
                const regToken = registerResponse.token || accessToken;
                const userData = {
                  id: regData.userId || registerResponse.userId || userEmail,
                  name: regData.username || userName,
                  email: userEmail,
                  logintype: 2,
                  usertype: regUsertype
                };
                connectWebSocket(regToken, userData.id);
                await completeLogin({ userData, token: regToken });
              } else {
                showToast(registerResponse.message || "Unable to create account. Please try again.", "error");
              }
            } catch (regError) {
              console.error("Google auto-register error:", regError);
              showToast("Unable to create account. Please try again.", "error");
            }
          } else {
            showToast("Unable to verify email. Please try again.", "error");
          }
        }
      } catch (error) {
        console.error("Google login error:", error);
        if (!error.message?.includes("Session Expired") &&
          !error.message?.includes("403")) {
          showToast("Failed to login with Google. Please try again.", "error");
        }
      } finally {
        setIsLoading(false);
      }
    },
    onError: () => {
      setIsLoading(false);
      showToast("Google login failed! Please try again.", "error");
    },
  });

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!email) {
      showToast("Please enter your email address", "warning");
      return;
    }
    if (!password) {
      showToast("Please enter your password", "warning");
      return;
    }

    setIsLoading(true);
    try {
      const verifyResponse = await postRequest(apiUrls.verifypassword, {
        email: email.trim(),
        password: password,
        app: 2
      });

      if (verifyResponse.status === "E" &&
        (verifyResponse.message?.includes("Session Expired") ||
          verifyResponse.message?.includes("logged in from another device"))) {
        showToast(verifyResponse.message, "error");
        return;
      }

      if (verifyResponse.status === "S" && verifyResponse.token) {
        const responseData = verifyResponse.data?.[0] || {};
        const usertype = responseData.usertype || verifyResponse.usertype || 2;

        if (usertype.toString() === "1") {
          showToast("Access denied for this user type", "error");
          return;
        }

        if (usertype.toString() !== "2" && usertype.toString() !== "3") {
          showToast("Email is invalid", "error");
          return;
        }

        const userName = localStorage.getItem(`username_${email}`) ||
          responseData.username ||
          verifyResponse.username ||
          email.split("@")[0];

        const userData = {
          id: responseData.userId || verifyResponse.userId || email,
          name: userName,
          email: email,
          logintype: 1,
          usertype: usertype
        };

        connectWebSocket(verifyResponse.token, userData.id);
        await completeLogin({ userData, token: verifyResponse.token });
      } else {
        showToast(verifyResponse.message || "Invalid email or password.", "error");
      }
    } catch (err) {
      console.error("Login error:", err);
      if (!err.message?.includes("Session Expired") && !err.message?.includes("403")) {
        showToast("Invalid email or password.", "error");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!username || username.trim() === "") {
      showToast("Full name is required", "warning");
      return;
    }
    if (!email) {
      showToast("Email address is required", "warning");
      return;
    }
    if (!password || password.length < 6) {
      showToast("Password must be at least 6 characters", "warning");
      return;
    }

    setIsLoading(true);
    try {
      const signupResponse = await postRequest(apiUrls.registeruser, {
        email: email.trim(),
        password: password,
        username: username.trim(),
        logintype: 1,
        app: 2,
        usertype: 2
      });

      if (signupResponse.status === "S") {
        localStorage.setItem(`username_${email}`, username);
        sessionStorage.setItem('prefillEmail', email);

        showToast("Account created successfully! Please login.", "success");

        setTimeout(() => {
          setStep("login");
          setPassword("");
        }, 400);

      } else if (signupResponse.status === "E") {
        showToast("Email already exists. Please login instead.", "error");
        setTimeout(() => setStep("login"), 1200);
      } else {
        showToast(signupResponse.message || "Unable to create account.", "error");
      }
    } catch (err) {
      console.error("Signup error:", err);
      if (!err.message?.includes("Session Expired") && !err.message?.includes("403")) {
        showToast("Unable to create account. Please try again.", "error");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    const targetEmail = email.trim() || "abina.abi1127@gmail.com";

    try {
      setIsLoading(true);
      const data = await postRequest(apiUrls.generateotp, { email: targetEmail });
      console.log("Generate OTP Response:", data);

      if (data.status === "S" || data.success) {
        const returnedUserId = data.userId || data.userid || data.data?.[0]?.userid || data.data?.[0]?.userId || 270;
        showToast(data.message || "OTP sent successfully!", "success");
        navigate("/verify-otp", { state: { email: targetEmail, userid: returnedUserId } });
      } else {
        showToast(data.message || "Failed to generate OTP", "error");
      }
    } catch (error) {
      console.error("Generate OTP Error:", error);
      showToast(error.message || "Failed to generate OTP. Please ensure the backend server is running.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      const isLoggedIn = localStorage.getItem("authToken");
      if (!isLoggedIn) {
        websocketService.disconnect();
      }
    };
  }, []);

  return (
    <>
      <div className="min-h-screen bg-[#FFFDF9] relative overflow-hidden flex flex-col items-center justify-center p-4 sm:p-6 py-10 selection:bg-orange-100 selection:text-orange-900">
        
        {/* Soft Background Warm Blur Elements */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none -z-10">
          <div className="absolute top-0 right-0 w-[450px] h-[450px] bg-orange-200/30 rounded-full blur-[100px] -translate-y-1/3 translate-x-1/4" />
          <div className="absolute top-1/3 left-0 w-[400px] h-[400px] bg-amber-100/40 rounded-full blur-[90px] -translate-x-1/3" />
          <div className="absolute bottom-0 right-1/4 w-[450px] h-[450px] bg-orange-100/35 rounded-full blur-[100px] translate-y-1/4" />
        </div>

        {/* Main Form Card */}
        <div className="w-full max-w-[440px] bg-white rounded-3xl border border-orange-100/70 shadow-[0_12px_45px_-12px_rgba(234,88,12,0.12)] overflow-hidden transition-all duration-300">
          
          {/* Card Top Bar */}
          <div className="p-6 pb-2 sm:p-7 sm:pb-3 flex items-center justify-between">
            {/* Logo / Brand Name */}
            <div 
              onClick={() => navigate("/")} 
              className="flex items-center gap-2 cursor-pointer group"
            >
              <img
                src={logo}
                alt="Ecom Tamil"
                className="h-10 w-auto object-contain transition-transform group-hover:scale-105"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = "none";
                }}
              />
            </div>

            {/* Mode Switch Pill Button */}
            {step === "login" ? (
              <button
                type="button"
                onClick={() => {
                  setStep("signup");
                  setError("");
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-orange-50/80 hover:bg-orange-100/80 border border-orange-100/80 text-[11px] font-medium text-orange-600 transition active:scale-95 cursor-pointer"
              >
                <User size={12} className="text-orange-500" />
                <span>New user? <strong className="text-orange-600 font-bold underline">Sign up</strong></span>
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setStep("login");
                  setError("");
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-orange-50/80 hover:bg-orange-100/80 border border-orange-100/80 text-[11px] font-medium text-orange-600 transition active:scale-95 cursor-pointer"
              >
                <User size={12} className="text-orange-500" />
                <span>Already have an account? <strong className="text-orange-600 font-bold underline">Login</strong></span>
              </button>
            )}
          </div>

          {/* Form Content Area */}
          <div className="p-6 pt-3 sm:p-7 sm:pt-4">
            
            {/* Heading Section */}
            <div className="mb-5">
              {step === "login" ? (
                <>
                  <div className="inline-flex items-center gap-1.5 text-xs font-bold text-orange-600 mb-1.5">
                    <span className="text-sm">🔒</span>
                    <span>Welcome back!</span>
                  </div>
                  <h1 className="text-2xl sm:text-[26px] font-black text-slate-800 tracking-tight leading-tight mb-1">
                    Login to your account
                  </h1>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Access your dashboard and continue your learning journey.
                  </p>
                </>
              ) : (
                <>
                  <div className="inline-flex items-center gap-1.5 text-xs font-bold text-orange-600 mb-1.5">
                    <span className="text-sm">👋</span>
                    <span>Welcome to Ecom Tamil</span>
                  </div>
                  <h1 className="text-2xl sm:text-[26px] font-black text-slate-800 tracking-tight leading-tight mb-1">
                    Create your account
                  </h1>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Join our community and start your e-commerce journey today.
                  </p>
                </>
              )}
            </div>

            {/* Form Fields */}
            <form onSubmit={step === "login" ? handleLoginSubmit : handleSignupSubmit} className="space-y-3.5">
              
              {/* Full Name field on Sign up */}
              {step === "signup" && (
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                    <Mail size={16} />
                  </div>
                  <input
                    type="text"
                    placeholder="Full name"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    disabled={isLoading}
                    className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all"
                  />
                </div>
              )}

              {/* Email Address */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                  <Mail size={16} />
                </div>
                <input
                  type="email"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isLoading}
                  className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all"
                />
              </div>

              {/* Password */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                  <Lock size={16} />
                </div>
                <input
                  ref={passwordRef}
                  type={showPassword ? "text" : "password"}
                  placeholder={step === "login" ? "Password" : "Create password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  className="w-full pl-10 pr-10 py-3 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600 transition cursor-pointer"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>

              {/* Forgot Password on Login */}
              {step === "login" && (
                <div className="flex items-center justify-end pt-0.5 text-xs">
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    className="text-[11px] sm:text-xs text-orange-600 hover:text-orange-700 font-medium transition cursor-pointer"
                  >
                    Forgot password?
                  </button>
                </div>
              )}

              {/* Password Requirements Checklist on Sign up */}
              {step === "signup" && (
                <div className="space-y-1 py-1 text-[11px]">
                  <div className={`flex items-center gap-1.5 transition-colors ${password.length >= 6 ? 'text-emerald-600 font-semibold' : 'text-slate-400'}`}>
                    <CheckCircle2 size={13} className={password.length >= 6 ? 'text-emerald-500' : 'text-slate-300'} />
                    <span>At least 6 characters</span>
                  </div>
                  <div className={`flex items-center gap-1.5 transition-colors ${/\d/.test(password) ? 'text-emerald-600 font-semibold' : 'text-slate-400'}`}>
                    <CheckCircle2 size={13} className={/\d/.test(password) ? 'text-emerald-500' : 'text-slate-300'} />
                    <span>Include a number</span>
                  </div>
                  <div className={`flex items-center gap-1.5 transition-colors ${/[A-Z]/.test(password) ? 'text-emerald-600 font-semibold' : 'text-slate-400'}`}>
                    <CheckCircle2 size={13} className={/[A-Z]/.test(password) ? 'text-emerald-500' : 'text-slate-300'} />
                    <span>Include an uppercase letter</span>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3 px-4 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold rounded-xl shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer active:scale-[0.99] text-xs sm:text-sm disabled:opacity-70"
                >
                  {isLoading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      <span>{step === "login" ? "Logging in..." : "Creating account..."}</span>
                    </>
                  ) : (
                    <>
                      <span>{step === "login" ? "Login" : "Create Account"}</span>
                      <ArrowRight size={16} />
                    </>
                  )}
                </button>
              </div>

              {/* Divider */}
              <div className="relative py-2">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200"></div>
                </div>
                <div className="relative flex justify-center text-[11px]">
                  <span className="bg-white px-3 text-slate-400">
                    {step === "login" ? "Or login with" : "Or sign up with"}
                  </span>
                </div>
              </div>

              {/* Google Button */}
              <button
                type="button"
                disabled={isLoading}
                onClick={loginWithGoogle}
                className="w-full py-2.5 px-4 bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50/80 rounded-xl text-xs font-bold text-slate-700 transition flex items-center justify-center gap-2.5 shadow-sm active:scale-[0.99] cursor-pointer"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
                <span>Continue with Google</span>
              </button>

              {/* Terms for Sign up */}
              {step === "signup" && (
                <p className="text-[10.5px] text-slate-400 text-center leading-relaxed pt-1">
                  By creating an account, you agree to our{" "}
                  <Link to="/terms_conditions" className="text-orange-600 font-semibold hover:underline">
                    Terms of Service
                  </Link>{" "}
                  and{" "}
                  <Link to="/privacy_policy" className="text-orange-600 font-semibold hover:underline">
                    Privacy Policy
                  </Link>.
                </p>
              )}
            </form>
          </div>

          {/* Card Bottom Feature Area */}
          {step === "login" ? (
            <div className="bg-gradient-to-r from-orange-50/70 via-amber-50/40 to-orange-50/70 border-t border-orange-100/70 p-4 px-6 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white border border-orange-200/80 flex items-center justify-center text-orange-600 shrink-0 shadow-sm">
                <Shield size={18} className="text-orange-500" />
              </div>
              <div>
                <h4 className="text-xs font-bold text-slate-800">Your data is safe with us</h4>
                <p className="text-[11px] text-slate-500 leading-tight mt-0.5">
                  We use industry-standard security to protect your information.
                </p>
              </div>
            </div>
          ) : (
            <div className="bg-gradient-to-r from-orange-50/70 via-amber-50/40 to-orange-50/70 border-t border-orange-100/70 p-4 px-5 flex items-center justify-around text-center">
              <div className="flex flex-col items-center gap-1">
                <div className="w-9 h-9 rounded-full bg-white border border-orange-200/80 flex items-center justify-center text-orange-500 shadow-sm">
                  <GraduationCap size={18} />
                </div>
                <span className="text-[10px] font-bold text-slate-700">Expert Guidance</span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <div className="w-9 h-9 rounded-full bg-white border border-orange-200/80 flex items-center justify-center text-orange-500 shadow-sm">
                  <Users size={18} />
                </div>
                <span className="text-[10px] font-bold text-slate-700">Community Support</span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <div className="w-9 h-9 rounded-full bg-white border border-orange-200/80 flex items-center justify-center text-orange-500 shadow-sm">
                  <TrendingUp size={18} />
                </div>
                <span className="text-[10px] font-bold text-slate-700">Grow Together</span>
              </div>
            </div>
          )}
        </div>

        {/* Need Help? Footer */}
        <div className="mt-7 text-center">
          <p className="text-[11px] text-slate-400 font-medium mb-1.5">Need help?</p>
          <div className="flex flex-wrap items-center justify-center gap-3 text-xs text-slate-600 font-medium">
            <a href="mailto:ecomtamil.official@gmail.com" className="flex items-center gap-1.5 hover:text-orange-600 transition">
              <Mail size={13} className="text-slate-400" />
              <span>ecomtamil.official@gmail.com</span>
            </a>
            <span className="text-slate-300">|</span>
            <a href="tel:+918939326064" className="flex items-center gap-1.5 hover:text-orange-600 transition">
              <Phone size={13} className="text-slate-400" />
              <span>+91 89393 26064</span>
            </a>
          </div>
        </div>

      </div>

      <CommonToastContainer />
    </>
  );
}